import { Map } from "immutable";
import { isNil, reduce, uniq } from "lodash";

import { AutoAdjustBackwardsLimit } from "../../../../../constants/autoAdjustBackwardsLimit";
import { AutoAdjustDateLevel } from "../../../../../constants/autoAdjustDateLevel";
import { createFromJS, immutableRecords, Precision } from "../../../../../types";
import dateConstraintReducer, { dateConstraintActionTypes } from "../../../../dateConstraints";
import { definitionActionTypes, definitionSelectors } from "../../../../definition";
import { calendarSelectors } from "../../../../definition/calendar";
import dateFunctions, { isDateValid, javascriptDateToAeonDate } from "../../../../definition/calendar/dateFunctions";
import dateAtEvenSpanBeforeDate from "../../../../definition/calendar/dateFunctions/dateAtEvenSpanBeforeDate";
import dateByAddingSpan from "../../../../definition/calendar/dateFunctions/dateByAddingSpan";
import dateWithLegalPrecision from "../../../../definition/calendar/dateFunctions/dateWithLegalPrecision";
import dateWithTimestamp from "../../../../definition/calendar/dateFunctions/dateWithTimestamp";
import {
  isTimespanNegative,
  isTimespanZero,
  timespanForPrecision
} from "../../../../definition/calendar/dateFunctions/timespanFunctions";
import timespanWithLegalPrecision from "../../../../definition/calendar/dateFunctions/timespanWithLegalPrecision";
import { typeSelectors } from "../../../../definition/types";
import dependencyReducer, { dependencyActionTypes } from "../../../../dependencies";
import displayCacheSelectors from "../../../../display/selectors";
import rootActionTypes from "../../../../rootActionTypes";
import { actionTypes } from "../../../actions";
import itemSelectors from "../../../selectors";
import isItemAffected from "../isItemAffected";
import closestValidDateForItem from "./closestValidDateForItem";
import { moveItemsAndDescendants } from "./moveItemsAndDescendants";
import { repairAndMoveAffectedItems } from "./repairAndMoveAffectedItems";
import { setEndDate, setItemEarliestEndDate, setItemPreferredEndDate } from "./setEndDate";
import { setEndToToday } from "./setEndToToday";
import { setItemDurationToEarliestEnd } from "./setItemDurationToEarliestEnd";
import { setItemLatestStartDate } from "./setItemLatestStartDate";
import { setItemPreferredDuration } from "./setItemPreferredDuration";
import { calculateViolatedItems, removeItemIdsOfNonDateTypes } from "./utils";

const changePrecisionForCalendar = (obj, calendar) => {
  let duration = timespanWithLegalPrecision(obj.startDate, obj.duration, calendar);
  let preferredDuration = timespanWithLegalPrecision(obj.preferredStartDate, obj.preferredDuration, calendar);
  let startDate = dateWithLegalPrecision(obj.startDate, calendar);
  let preferredStartDate = dateWithLegalPrecision(obj.preferredStartDate, calendar);
  return obj.merge({
    duration,
    preferredDuration,
    startDate,
    preferredStartDate
  });
};

export const setTimespanProp = (obj, timespan, propName) => {
  if (propName) {
    if (!timespan || !isTimespanNegative(timespan)) {
      return obj.set(propName, timespan ? createFromJS.TimespanRecord(timespan) : timespan);
    }
  }
  return obj;
};

// const setItemDuration = (obj, duration) => setTimespanProp(obj, duration, "duration");

const clearItemTimeValues = obj => {
  return obj.withMutations(o => {
    const emptyDate = immutableRecords.DateRecord();
    const emptyDuration = immutableRecords.TimespanRecord();
    o.set("startDate", emptyDate);
    o.set("preferredStartDate", emptyDate);
    o.set("latestStartDate", emptyDate);
    o.set("duration", emptyDuration);
    o.set("preferredDuration", emptyDuration);
    o.set("durationToEarliestEnd", emptyDuration);
    o.set("ongoing", false);
    o.set("isDateLocked", false);
  });
};

const setItemIsOngoing = (obj, ongoing, calendar, nowTimestamp) => {
  const oldOngoing = obj.ongoing;
  let newObj = obj;
  if (ongoing !== oldOngoing) {
    if (ongoing === false) {
      newObj = setEndToToday(obj, calendar, nowTimestamp);
    } else {
      newObj = setEndDate(obj, null, -1, calendar, "preferredStartDate", "preferredDuration");
    }
    return (newObj = newObj.set("ongoing", ongoing));
  }
  return newObj;
};

const getTargetDatesForSpanMove = (itemsById, ids, calendar, span) => {
  return reduce(
    ids,
    (result, id) => {
      const item = itemsById.get(id);
      result[id] = isDateValid(item.preferredStartDate)
        ? dateByAddingSpan(calendar, item.preferredStartDate, span)
        : immutableRecords.DateRecord();
      return result;
    },
    {}
  );
};

const handleChangeStartAction = (state, action, calendar, autoAdjustLevel) => {
  let newDatesById;
  let minAdjustLevel = AutoAdjustDateLevel.Off;
  const spanChange = action.spanChange ? immutableRecords.TimespanRecord(action.spanChange) : null;
  if (action.type === actionTypes.MOVE_BY_TIMESPAN) {
    newDatesById = getTargetDatesForSpanMove(state, action.ids, calendar, spanChange);
    minAdjustLevel = AutoAdjustDateLevel.AdjustSelf;
  } else {
    const targetDate = isNil(action.timestamp)
      ? immutableRecords.DateRecord()
      : dateWithTimestamp(calendar, action.timestamp, action.precision);
    newDatesById = reduce(
      action.ids,
      (result, id) => {
        result[id] = targetDate;
        return result;
      },
      {}
    );
  }

  return moveItemsAndDescendants(
    state,
    calendar,
    newDatesById,
    spanChange,
    Math.max(autoAdjustLevel, minAdjustLevel),
    true
  );
};

const handleBulkChangeAction = (state, action, calendar, autoAdjustLevel) => {
  let minAdjustLevel = AutoAdjustDateLevel.Off;
  const spanChange = null;
  const targetDate = isNil(action.startDate.timestamp)
    ? immutableRecords.DateRecord()
    : dateWithTimestamp(calendar, action.startDate.timestamp, action.startDate.precision);
  const newDatesById = reduce(
    action.ids,
    (result, id) => {
      result[id] = targetDate;
      return result;
    },
    {}
  );
  // First, update the start date, and let it go ahead and update all of the
  // internal values...
  // We will override the others passed in here once it returns.
  let { itemsById, movedIds } = moveItemsAndDescendants(
    state,
    calendar,
    newDatesById,
    spanChange,
    Math.max(autoAdjustLevel, minAdjustLevel),
    true
  );

  movedIds = uniq([...action.ids, ...movedIds]);

  // Update the latest start date
  itemsById = itemsById.map(obj =>
    isItemAffected(obj, action.ids)
      ? setItemLatestStartDate(obj, action.latestStartDate.timestamp, action.latestStartDate.precision, calendar)
      : obj
  );

  // Update the earliest end date
  itemsById = itemsById.map(obj =>
    isItemAffected(obj, action.ids)
      ? setItemEarliestEndDate(obj, action.earliestEndDate.timestamp, action.earliestEndDate.precision, calendar)
      : obj
  );

  // Update the end date
  itemsById = itemsById.map(obj =>
    isItemAffected(obj, action.ids)
      ? setItemPreferredEndDate(obj, action.endDate.timestamp, action.endDate.precision, calendar)
      : obj
  );

  return { itemsById, movedIds };
};

const getItemsAffectedBySuperChange = (itemsById, prevItemsByid, ids) => {
  return reduce(
    ids,
    (result, id) => {
      const newItem = itemsById.get(id);
      const oldItem = prevItemsByid.get(id);
      const newSuper = newItem && newItem.super ? itemsById.get(newItem.super) : null;
      const oldSuper = oldItem && oldItem.super ? prevItemsByid.get(oldItem.super) : null;
      if (newSuper) {
        result.push(newSuper.id);
      }
      if (oldSuper) {
        result.push(oldSuper.id);
      }
      return result;
    },
    []
  );
};

/**
 * Returns movableIds, except where it needs to calculate them
 * @param {object} action
 * @param {TimelineRecord} previousGlobalState
 * @param {array} movableIds
 */
const getItemsAffectedByDependencyChange = (action, previousGlobalState, movableIds) => {
  // When calling REMOVE_DEPENDENCY, we need to find the item IDs out of the
  // dependency being edited.
  if (action.type === dependencyActionTypes.REMOVE_DEPENDENCIES) {
    const dependenciesById = previousGlobalState.data.dependencies.byId;
    return reduce(
      action.ids,
      (result, id) => {
        const dependency = dependenciesById.get(id);
        result.push(dependency.subject);
        result.push(dependency.object);
        return result;
      },
      []
    );
  }

  // Otherwise, return the ones that are movable
  return movableIds;
};

const getDatelessItemsFromDependenciesOrConstraints = (depOrConstraintsById, itemsById, dcIds) => {
  return reduce(
    dcIds,
    (result, dId) => {
      const dc = depOrConstraintsById.get(dId);
      const subject = dc && dc.subject ? itemsById.get(dc.subject) : null;
      const object = dc && dc.object ? itemsById.get(dc.object) : null;
      if (subject && !isDateValid(subject.startDate)) {
        result.push(subject.id);
      }
      if (object && !isDateValid(object.startDate)) {
        result.push(object.id);
      }
      return result;
    },
    []
  );
};

const getTargetDatesForManualResolution = (itemsById, globalState, movableIds, otherIds, backwardsLimitDate) => {
  return reduce(
    movableIds,
    (result, id) => {
      result[id] = closestValidDateForItem(itemsById, globalState, id, otherIds, backwardsLimitDate);
      return result;
    },
    {}
  );
};

const getBackwardsLimitDate = (action, calendar, previousGlobalState) => {
  const backwardsLimit = definitionSelectors.getAutoAdjustBackwardsLimit(previousGlobalState);
  if (backwardsLimit === AutoAdjustBackwardsLimit.Off) {
    return null;
  }

  const nowDate = !isNil(calendar.overrideNowTimestamp)
    ? dateFunctions.dateWithTimestamp(calendar, calendar.overrideNowTimestamp)
    : javascriptDateToAeonDate(calendar, action.actionAppliedJsDate);

  switch (backwardsLimit) {
    case AutoAdjustBackwardsLimit.Today:
      return dateAtEvenSpanBeforeDate(calendar, nowDate, timespanForPrecision(Precision.Day));
    case AutoAdjustBackwardsLimit.Tomorrow:
      return dateByAddingSpan(
        calendar,
        dateAtEvenSpanBeforeDate(calendar, nowDate, timespanForPrecision(Precision.Day)),
        timespanForPrecision(Precision.Day)
      );
    case AutoAdjustBackwardsLimit.NextWeek:
      return dateByAddingSpan(
        calendar,
        dateAtEvenSpanBeforeDate(calendar, nowDate, timespanForPrecision(Precision.Week)),
        timespanForPrecision(Precision.Week)
      );
    case AutoAdjustBackwardsLimit.NextMonth:
      return dateByAddingSpan(
        calendar,
        dateAtEvenSpanBeforeDate(calendar, nowDate, timespanForPrecision(Precision.Month)),
        timespanForPrecision(Precision.Month)
      );
  }

  return null;
};

export default (state = Map(), action, previousGlobalState) => {
  const calendar = calendarSelectors.getCalendar(previousGlobalState);
  let autoAdjustLevel = definitionSelectors.getAutoAdjustLevel(previousGlobalState);

  let newState = state;
  let newGlobalState = previousGlobalState;
  let checkIds = [];
  let recalculateCheckIds = false;
  let iterateToResolve = false;

  // Note: Within this switch statement, we do not need to worry about fixing child ranges
  // or adjusting dependencies. We will do this in a separate function afterwards if required.
  // The loop does need to account for moving children when required though.

  switch (action.type) {
    case actionTypes.MOVE_BY_TIMESPAN:
    case actionTypes.SET_PREFERRED_START_DATE: {
      const moveResult = handleChangeStartAction(state, action, calendar, autoAdjustLevel);
      newState = moveResult.itemsById;
      checkIds = moveResult.movedIds;
      break;
    }
    case actionTypes.SET_BULK_PREFERRED_DATES: {
      const moveResult = handleBulkChangeAction(state, action, calendar, autoAdjustLevel);
      newState = moveResult.itemsById;
      checkIds = moveResult.movedIds;
      break;
    }
    case actionTypes.SET_ONGOING:
      newState = state.map(obj =>
        isItemAffected(obj, action.ids)
          ? setItemIsOngoing(
            obj,
            action.ongoing,
            calendar,
            javascriptDateToAeonDate(calendar, action.actionAppliedJsDate).timestamp
          )
          : obj
      );
      checkIds = [...action.ids];
      break;
    case actionTypes.SET_LATEST_START_DATE:
      newState = state.map(obj =>
        isItemAffected(obj, action.ids)
          ? setItemLatestStartDate(obj, action.timestamp, action.precision, calendar)
          : obj
      );
      checkIds = [...action.ids];
      break;
    case actionTypes.SET_PREFERRED_DURATION: {
      newState = state.map(obj => {
        // Should not update duration explicitly from actions (only allow updating by changing end date)
        return isItemAffected(obj, action.ids) && !isDateValid(obj.latestStartDate) && !obj.earliestEndDate
          ? setItemPreferredDuration(obj, action.duration, calendar)
          : obj;
      });
      checkIds = [...action.ids];
      break;
    }
    case actionTypes.SET_DURATION_TO_EARLIEST_END: {
      newState = state.map(obj =>
        isItemAffected(obj, action.ids) ? setItemDurationToEarliestEnd(obj, action.duration, calendar) : obj
      );
      checkIds = [...action.ids];
      break;
    }
    case actionTypes.ADJUST_PRECISION_FOR_CALENDAR_TYPE: {
      newState = state.map(obj =>
        isItemAffected(obj, action.ids) ? changePrecisionForCalendar(obj, previousGlobalState, calendar) : obj
      );
      checkIds = [...action.ids];
      break;
    }
    case actionTypes.SET_EARLIEST_END_DATE:
      newState = state.map(obj =>
        isItemAffected(obj, action.ids)
          ? setItemEarliestEndDate(obj, action.timestamp, action.precision, calendar)
          : obj
      );
      checkIds = [...action.ids];
      break;
    case actionTypes.SET_PREFERRED_END_DATE:
      newState = state.map(obj =>
        isItemAffected(obj, action.ids)
          ? setItemPreferredEndDate(obj, action.timestamp, action.precision, calendar)
          : obj
      );
      checkIds = [...action.ids];
      break;
    case actionTypes.CLEAR_TIME_VALUES:
      newState = state.map(obj => (isItemAffected(obj, action.ids) ? clearItemTimeValues(obj) : obj));
      checkIds = [...action.ids];
      break;
    case actionTypes.SET_SUPER:
    case actionTypes.DELETE_SUPER:
    case actionTypes.SET_CLOSEST_VALID_ANCESTOR:
    case actionTypes.DELETE_ITEMS: {
      // Note: Actual super changes have been applied already by the main reducer before we reach this
      // date-specific function, which means:
      // 1) We don't have to change the supers here
      // 2) We need to look at the previous vs current state to get all affected supers
      checkIds = getItemsAffectedBySuperChange(state, previousGlobalState.data.items.byId, action.ids);
      break;
    }
    case dependencyActionTypes.CREATE_DEPENDENCIES:
    case dependencyActionTypes.CREATE_CHAIN_DEPENDENCIES:
    case dependencyActionTypes.REMOVE_DEPENDENCIES:
    case dependencyActionTypes.REMOVE_DEPENDENCIES_WITH_SUBJECTS:
    case dependencyActionTypes.REMOVE_DEPENDENCIES_WITH_OBJECTS:
    case dependencyActionTypes.REMOVE_DEPENDENCIES_WITH_VALUES:
    case dependencyActionTypes.CHANGE_DEPENDENCY_TYPE_WITH_VALUES: {
      // For changes to dependencies:
      // Let's first get a copy of what the new state will be.
      // This is a little inefficient since it is running the reducer an extra time,
      // but that reducer is pretty simple, so it will be more efficient (and easier logic)
      // than iterating through validators...

      if (autoAdjustLevel >= AutoAdjustDateLevel.ResolveConstraints) {
        newGlobalState = newGlobalState.updateIn(["data", "dependencies"], dependencies =>
          dependencyReducer(dependencies, action, previousGlobalState)
        );
        checkIds = getItemsAffectedByDependencyChange(action, previousGlobalState, action.movableItems);

        // When creating a dependency, we need to add in the other half of the dependency if it is
        // dateless to ensure it will be given a date, even if the selected item does not move
        // (repairAndMoveAffectedItems only looks at dependencies when an item moves, and creating
        // a dependency to a dateless item will not trigger a move)
        if (
          action.type === dependencyActionTypes.CREATE_DEPENDENCIES ||
          action.type === dependencyActionTypes.CREATE_CHAIN_DEPENDENCIES
        ) {
          checkIds = uniq(
            checkIds.concat(
              getDatelessItemsFromDependenciesOrConstraints(
                newGlobalState.data.dependencies.byId,
                newGlobalState.data.items.byId,
                action.ids
              )
            )
          );
        }

        recalculateCheckIds = true;
      }
      break;
    }
    case dateConstraintActionTypes.CREATE_CONSTRAINTS:
    case dateConstraintActionTypes.REMOVE_CONSTRAINTS: {
      // For changes to dateConstraints:
      // Let's first get a copy of what the new state will be.
      // This is a little inefficient since it is running the reducer an extra time,
      // but that reducer is pretty simple, so it will be more efficient (and easier logic)
      // than iterating through validators...

      if (autoAdjustLevel >= AutoAdjustDateLevel.ResolveConstraints) {
        newGlobalState = newGlobalState.updateIn(["data", "dateConstraints"], dateConstraints =>
          dateConstraintReducer(dateConstraints, action, previousGlobalState)
        );
        checkIds = action.movableItems ? action.movableItems : [];

        // When creating a constraint, we need to add in the other half of the constraint if it is
        // dateless to ensure it will be given a date, even if the selected item does not move
        // (repairAndMoveAffectedItems only looks at constraints when an item moves, and creating
        // a dependency to a dateless item will not trigger a move)
        if (action.type === dateConstraintActionTypes.CREATE_CONSTRAINTS) {
          checkIds = uniq(
            checkIds.concat(
              getDatelessItemsFromDependenciesOrConstraints(
                newGlobalState.data.dateConstraints.byId,
                newGlobalState.data.items.byId,
                action.ids
              )
            )
          );
        }

        recalculateCheckIds = true;
      }
      break;
    }
    case actionTypes.RESOLVE_CONSTRAINT_MANUALLY: {
      // Manually resolving constraint requires us to:
      // 1) Lookup the constraint
      // 2) Calculate the correct date to resolve that constraint
      const backwardsLimitDate = getBackwardsLimitDate(action, calendar, previousGlobalState);
      const newDatesById = getTargetDatesForManualResolution(
        newState,
        previousGlobalState,
        action.movableItems,
        action.otherItems,
        backwardsLimitDate
      );

      const moveResult = moveItemsAndDescendants(
        state,
        calendar,
        newDatesById,
        null,
        Math.max(autoAdjustLevel, AutoAdjustDateLevel.AdjustSelf),
        false // do not change preferredPrecision for automatic calculations
      );
      newState = moveResult.itemsById;
      checkIds = moveResult.movedIds;
      break;
    }
    case actionTypes.RESOLVE_ALL_CONSTRAINTS: {
      checkIds = displayCacheSelectors.getViolatedItems(previousGlobalState);
      recalculateCheckIds = true;
      iterateToResolve = true;
      break;
    }
    case actionTypes.DRAG_START_DATE_WITH_FIXED_END: {
      // Don't allow item's start to be moved beyond its end date and ensure item has a date
      const isActionDatePastItemEnd = state.find(obj =>
        isItemAffected(obj, action.ids)
          ? !isDateValid(obj.preferredStartDate) ||
            (obj.duration &&
              dateByAddingSpan(calendar, obj.preferredStartDate, obj.duration).timestamp < action.timestamp)
          : false
      );

      if (!isActionDatePastItemEnd) {
        newState = state.map(obj => {
          let newObj = obj;
          if (isItemAffected(newObj, action.ids)) {
            // Move earliest end if its later than the incoming start date
            if (
              newObj.durationToEarliestEnd &&
              !isTimespanZero(newObj.durationToEarliestEnd) &&
              dateByAddingSpan(calendar, newObj.preferredStartDate, newObj.durationToEarliestEnd).timestamp <
                action.timestamp
            ) {
              newObj = setItemEarliestEndDate(newObj, action.timestamp, action.precision, calendar);
            }
            // Remove latest start if its later than the incoming start date
            if (isDateValid(newObj.latestStartDate) && newObj.latestStartDate.timestamp < action.timestamp) {
              newObj = setItemLatestStartDate(newObj, null, null, calendar);
            }
          }
          return newObj;
        });
        // Set the start date forcing no auto adjustment
        const moveResult = handleChangeStartAction(newState, action, calendar, AutoAdjustDateLevel.Off);
        newState = moveResult.itemsById;
        checkIds = moveResult.movedIds;
      }

      break;
    }
    case actionTypes.DRAG_END_DATE_WITH_FIXED_START: {
      // Don't allow item's end to be moved before its start date and ensure item has a date
      const isActionDateBeforeItemStart = state.find(obj =>
        isItemAffected(obj, action.ids)
          ? !isDateValid(obj.preferredStartDate) ||
            (isDateValid(obj.preferredStartDate) && obj.preferredStartDate.timestamp > action.timestamp)
          : false
      );

      if (!isActionDateBeforeItemStart) {
        newState = state.map(obj => {
          let newObj = obj;
          if (isItemAffected(newObj, action.ids)) {
            // Move latest start if its earlier than the incoming end date
            if (isDateValid(newObj.latestStartDate) && newObj.latestStartDate.timestamp > action.timestamp) {
              newObj = setItemLatestStartDate(newObj, action.timestamp, action.precision, calendar);
            }
            // Remove earliest end if its earlier than the incoming end date
            if (isDateValid(newObj.earliestEndDate) && newObj.earliestEndDate.timestamp > action.timestamp) {
              newObj = setItemEarliestEndDate(newObj, null, null, calendar);
            }
            // Set the end date
            newObj = setItemPreferredEndDate(newObj, action.timestamp, action.precision, calendar);
          }
          return newObj;
        });
        checkIds = [...action.ids];
      }

      break;
    }
    case definitionActionTypes.REPLACE_DEFINITIONS: {
      // When turning on Auto-Resolve constraints, we should try to resolve any outstanding constraints
      if (
        definitionSelectors.getAutoAdjustLevel(previousGlobalState) < AutoAdjustDateLevel.ResolveConstraints &&
        definitionSelectors.getAutoAdjustLevel(action) === AutoAdjustDateLevel.ResolveConstraints
      ) {
        newGlobalState = newGlobalState.set("definitions", action.definitions);
        autoAdjustLevel = definitionSelectors.getAutoAdjustLevel(newGlobalState);
        checkIds = displayCacheSelectors.getViolatedItems(previousGlobalState);
        recalculateCheckIds = true;
        iterateToResolve = true;
        break;
      } else {
        return newState;
      }
    }
    case actionTypes.UPDATE_ITEM: {
      checkIds = action.ids;
      break;
    }
    case rootActionTypes.CONVERT_STANDARD: {
      const childIds = itemSelectors
        .getItemsWithParent(previousGlobalState)
        .map(item => item.id)
        .toList()
        .toArray();
      checkIds = childIds;
      break;
    }
    default:
      return newState;
  }

  // Once we have applied the core change requested, we then want to check and move any affected items.
  // This includes:
  // 1) Recalculating child ranges for all affected items and their supers
  // 2) Finding any strict or loose dependencies/constraints, and fixing those

  // Filter out the items whose type cannot have date
  checkIds = removeItemIdsOfNonDateTypes(checkIds, newState, typeSelectors.getTypesById(newGlobalState));

  if (checkIds && checkIds.length) {
    let iteration = 0;
    const MAX_ITERATIONS = 5;

    const backwardsLimitDate = getBackwardsLimitDate(action, calendar, newGlobalState);

    // We always execute this at least once
    do {
      newState = repairAndMoveAffectedItems(
        newState,
        newGlobalState,
        checkIds,
        recalculateCheckIds,
        Math.max(autoAdjustLevel, AutoAdjustDateLevel.AdjustSelf),
        backwardsLimitDate
      );

      // If we are attempting to resolve all constraints, then we need to prepare for
      // another loop.
      if (iterateToResolve) {
        // Update the global state with the changed items, so that we can check for
        // violated items again
        newGlobalState = newGlobalState.setIn(["data", "items", "byId"], newState);

        // And find any remaining IDs that are not fixed
        // We used to use a selector for this, but now we have to do our
        // own manual calculation since they are inside a reducer and not
        // updated yet
        checkIds = calculateViolatedItems(newGlobalState);
        // checkIds = validatedItemSelectors.getViolatedItems(newGlobalState);

        // Filter out the items that their type cannot have date
        checkIds = removeItemIdsOfNonDateTypes(checkIds, newState, typeSelectors.getTypesById(newGlobalState));

        iteration++;
        iterateToResolve = checkIds.length > 0 && iteration < MAX_ITERATIONS;
      }
    } while (iterateToResolve);
  }

  return newState;
};
